import { useCallback, useEffect, useRef, type DragEvent, type MouseEvent } from 'react';
import {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  type Connection,
  type NodeChange,
} from '@xyflow/react';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { nodeKindMeta, type LifecycleStage, type ThemeMode } from '@/theme/tokens';
import type { OlEdge, OlNode, OlNodeData } from '@/flow-ir/toReactFlow';
import { laneAt, LAYOUT_CONST, type FlowLayout } from '@/flow-ir/layout';
import type { XY } from '@/store/designerSlice';
import { nodeTypes } from './nodes';
import { edgeTypes } from './edges';
import { LaneBackground } from './LaneBackground';
import { MarkerDefs } from './edges/markers';
import { PALETTE_MIME } from './Palette';
import './designer.css';

/** A one-shot pan/zoom request (Problems/Diff row → canvas). `nonce` distinguishes repeats. */
export interface CanvasFocusTarget {
  id: string;
  kind: 'node' | 'edge';
  nonce: number;
}

export interface DesignerCanvasProps {
  nodes: OlNode[];
  edges: OlEdge[];
  layout: FlowLayout;
  colorMode: ThemeMode;
  editable: boolean;
  /** True when a palette entry is armed for click/keyboard placement. */
  placing: boolean;
  onSelectNode: (id: string | null) => void;
  onSelectEdge: (id: string | null) => void;
  onNodeMove?: (id: string, pos: XY) => void;
  onNodeDragStop?: (id: string, pos: XY, laneStage: LifecycleStage) => void;
  onConnectEdge?: (conn: Connection) => void;
  onDropEntry?: (paletteKey: string, stage: LifecycleStage, pos: XY) => void;
  onPlaceInLane?: (stage: LifecycleStage) => void;
  onPanePlace?: (stage: LifecycleStage, pos: XY) => void;
  onPaneClear?: () => void;
  /** Minimap visibility (default true). Toggle button renders only when a handler is provided. */
  minimapVisible?: boolean;
  onToggleMinimap?: () => void;
  /** Pan/zoom the viewport to a node or an edge's endpoints (click-to-focus from panels). */
  focusTarget?: CanvasFocusTarget | null;
}

function CanvasInner(props: DesignerCanvasProps) {
  const {
    nodes,
    edges,
    layout,
    colorMode,
    editable,
    placing,
    onSelectNode,
    onSelectEdge,
    onNodeMove,
    onNodeDragStop,
    onConnectEdge,
    onDropEntry,
    onPlaceInLane,
    onPanePlace,
    onPaneClear,
    minimapVisible = true,
    onToggleMinimap,
    focusTarget,
  } = props;
  const rf = useReactFlow();

  // ---- click-to-focus: pan/zoom to the diagnostic's element (one-shot per nonce) ----------------
  const handledFocus = useRef(0);
  useEffect(() => {
    if (!focusTarget || focusTarget.nonce === handledFocus.current) return;
    handledFocus.current = focusTarget.nonce;
    const ids =
      focusTarget.kind === 'edge'
        ? (() => {
            const e = edges.find((x) => x.id === focusTarget.id);
            return e ? [e.source, e.target] : [];
          })()
        : [focusTarget.id];
    if (ids.length === 0) return;
    const reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    void rf.fitView({
      nodes: ids.map((id) => ({ id })),
      padding: 0.4,
      maxZoom: 1.15,
      duration: reduceMotion ? 0 : 260,
    });
  }, [focusTarget, edges, rf]);

  const handleNodesChange = useCallback(
    (changes: NodeChange<OlNode>[]) => {
      if (!editable || !onNodeMove) return;
      for (const c of changes) {
        if (c.type === 'position' && c.position) onNodeMove(c.id, c.position);
      }
    },
    [editable, onNodeMove],
  );

  const handleDragOver = useCallback(
    (e: DragEvent) => {
      if (!editable) return;
      if (e.dataTransfer.types.includes(PALETTE_MIME)) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
      }
    },
    [editable],
  );

  const handleDrop = useCallback(
    (e: DragEvent) => {
      if (!editable || !onDropEntry) return;
      const key = e.dataTransfer.getData(PALETTE_MIME);
      if (!key) return;
      e.preventDefault();
      const pos = rf.screenToFlowPosition({ x: e.clientX, y: e.clientY });
      onDropEntry(key, laneAt(layout, pos.x + LAYOUT_CONST.NODE_W / 2), pos);
    },
    [editable, onDropEntry, rf, layout],
  );

  const handlePaneClick = useCallback(
    (e: MouseEvent) => {
      if (placing && onPanePlace) {
        const pos = rf.screenToFlowPosition({ x: e.clientX, y: e.clientY });
        onPanePlace(laneAt(layout, pos.x + LAYOUT_CONST.NODE_W / 2), pos);
      } else {
        onPaneClear?.();
      }
    },
    [placing, onPanePlace, onPaneClear, rf, layout],
  );

  return (
    <div
      className="ol-canvas-wrap"
      style={{ height: '100%', width: '100%' }}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={handleNodesChange}
        nodesDraggable={editable}
        nodesConnectable={editable}
        elementsSelectable
        nodesFocusable
        edgesFocusable
        deleteKeyCode={null}
        fitView
        fitViewOptions={{ padding: 0.18 }}
        minZoom={0.2}
        maxZoom={2}
        colorMode={colorMode}
        proOptions={{ hideAttribution: true }}
        onNodeClick={(_, n) => onSelectNode(n.id)}
        onEdgeClick={(_, e) => onSelectEdge(e.id)}
        onPaneClick={handlePaneClick}
        onConnect={(conn) => onConnectEdge?.(conn)}
        onNodeDragStop={(_, n) =>
          onNodeDragStop?.(n.id, n.position, laneAt(layout, n.position.x + LAYOUT_CONST.NODE_W / 2))
        }
        className={`ol-canvas${editable ? ' ol-canvas--editable' : ''}`}
        aria-label="Workflow designer canvas"
      >
        <LaneBackground layout={layout} placing={placing} onPlaceInLane={onPlaceInLane} />
        <Background variant={BackgroundVariant.Dots} gap={24} size={1} color="var(--ol-border-subtle)" />
        {minimapVisible && (
          <MiniMap
            pannable
            zoomable
            ariaLabel="Flow minimap"
            nodeColor={(n) => nodeKindMeta((n.data as OlNodeData).node.kind).accent}
            nodeStrokeColor="transparent"
            nodeBorderRadius={3}
            maskColor="color-mix(in srgb, var(--ol-bg-base) 62%, transparent)"
          />
        )}
        {onToggleMinimap && (
          <Panel position="bottom-right" style={{ marginBottom: minimapVisible ? 158 : 10 }}>
            <button
              type="button"
              className="ol-minimap-toggle"
              onClick={onToggleMinimap}
              aria-pressed={minimapVisible}
              aria-label={minimapVisible ? 'Hide minimap (M)' : 'Show minimap (M)'}
              title={minimapVisible ? 'Hide minimap (M)' : 'Show minimap (M)'}
            >
              {minimapVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />} minimap
            </button>
          </Panel>
        )}
        <Controls showInteractive={false} />
        <MarkerDefs />
      </ReactFlow>
    </div>
  );
}

/** The Workflow Designer canvas — read-only on the sample path, fully editable when a flow is loaded. */
export function DesignerCanvas(props: DesignerCanvasProps) {
  return (
    <ReactFlowProvider>
      <CanvasInner {...props} />
    </ReactFlowProvider>
  );
}
